import { useController } from "react-hook-form";
import { spacing } from "../../../../../../../../stitches.config";
import { Button } from "../../../../../../../Common/Buttons/Button";
import { Label } from "../../../../../../../Inputs";
import Wrapper from "../../../../../../../Inputs/Wrapper";
import {
  getSchemaPropertyFormPath,
  useBuilderFormContext,
} from "../../../form/form";
import { FieldWrapper } from "../../__styles__/SidebarColumn";
import {
  HelpText,
  WidgetSettingsContainer,
} from "../__styles__/WidgetSettings";
import type { InputSettingsFormProps } from "../FieldSettingsView";
import { DefaultValuesSection } from "../shared/DefaultValuesSection";
import { DisplaySettings } from "../shared/DisplaySettings";
import { FieldPermissionsConfig } from "../shared/FieldPermissionsConfig";
import { FieldSettingsConfig } from "../shared/FieldSettingsConfig";
import { OptionsInput } from "./OptionsInput";
import { TagsStaticDefaultInput } from "./TagsStaticDefaultInput";
import { useEnsureValidDefaultValue } from "./utils";

export function TagsWidgetSettings({
  fieldPath,
  isModuleInput,
  isReadOnly,
}: InputSettingsFormProps) {
  const { setValue } = useBuilderFormContext();

  const optionsPath = getSchemaPropertyFormPath(fieldPath, "items.enum");
  const { field, fieldState } = useController({ name: optionsPath });

  const options = (field.value as Array<string>).length ? field.value : [""];

  const ensureValidDefaultValue = useEnsureValidDefaultValue({
    fieldPath,
    setValue,
  });

  function addOption() {
    const newOptions = [...options, ""];
    field.onChange(newOptions);
  }

  function updateOption(index: number, value: string) {
    const newOptions = [...options];
    newOptions[index] = value;

    ensureValidDefaultValue(newOptions);
    field.onChange(newOptions);
  }

  function removeOption(index: number) {
    const newOptions = [...options];
    newOptions.splice(index, 1);

    ensureValidDefaultValue(newOptions);
    field.onChange(newOptions);
  }

  const isDisabled = isModuleInput || isReadOnly;
  const minItemsPath = getSchemaPropertyFormPath(fieldPath, "minItems");

  return (
    <WidgetSettingsContainer>
      <FieldSettingsConfig
        fieldPath={fieldPath}
        isModuleInput={isModuleInput}
        isReadOnly={isReadOnly}
        isFormulaField={false}
        onRequiredChange={e => {
          if (e.target.checked) {
            setValue(minItemsPath, 1, { shouldValidate: true });
          } else {
            setValue(minItemsPath, 0, { shouldValidate: true });
          }
        }}
      />
      <div data-testid="pill-options-wrapper">
        <div>
          <Label
            css={{ marginBottom: spacing.xs }}
            required
            text="Pill options"
          />
          <HelpText style={{ marginTop: 0, paddingBottom: spacing.s.value }}>
            Provide options for users to select from; enter each option on a new
            field
          </HelpText>
        </div>
        <Wrapper
          name={optionsPath}
          error={fieldState.error?.message}
          disabled={isDisabled}
        >
          <OptionsInput
            options={options}
            updateOption={updateOption}
            removeOption={removeOption}
            disabled={isDisabled}
          />
          <div style={{ paddingTop: spacing.s.value }}>
            <Button
              title="Add option"
              variant="outline"
              size="small"
              iconStart="plus"
              onPress={addOption}
              isDisabled={isDisabled}
            />
          </div>
        </Wrapper>
      </div>
      <DefaultValuesSection>
        <FieldWrapper data-testid="static-default-value-wrapper">
          <TagsStaticDefaultInput
            fieldPath={fieldPath}
            isReadOnly={isReadOnly}
          />
        </FieldWrapper>
      </DefaultValuesSection>
      <FieldPermissionsConfig fieldPath={fieldPath} isReadOnly={isReadOnly} />
      <DisplaySettings fieldPath={fieldPath} isReadOnly={isReadOnly} />
    </WidgetSettingsContainer>
  );
}
